/**
 * `<EditorSurface>` — the in-app authoring surface root (editor-chat.md §1.2, §2.1, §3.1).
 *
 * The route element mounted at `/editor/:noteId?` (the frontend owns the route *slot* via the
 * `ShellMount`; this workstream owns the *element* — ADR 007 / routes.ts). It:
 *  - loads the note via `get_note` (the existing `useNoteQuery`) — body-only, no companion
 *    (the editor only needs `id, title, body, source` — editor-chat.md §3.2);
 *  - owns the `AutocompleteSourceStore` (via `useAutocompleteSources`) for the lifetime of the
 *    editor and hands it to `<EditorViewHost>`;
 *  - composes the split pane: `<EditorViewHost>` (source, left) + `<PreviewPane>` (right),
 *    wired by `useScrollSync` (editor-chat.md §1.2 — split-pane live preview);
 *  - owns the **D-007 save path**: the injected `onSave` issues `update_note` /
 *    `PUT /api/v1/notes/{id}`; the backend sets `locallyModifiedAt` for a Joplin note and
 *    returns the `SaveResult` — the surface reflects dirty/saved/conflicted in its status
 *    affordance (`<EditorStatusBar>`), it does not own the state machine (editor-chat.md §1.6);
 *  - owns **drop routing**: `.md` → the ingestion markdown-import endpoint; non-`.md` →
 *    the attachment path (surfaced as `attachment-pending` until the REVIEW M1 contract lands
 *    — editor-chat.md §3.1 / §6 OQ-5; never a silent drop).
 *
 * D-007 is load-bearing: a `source='joplin'` note opens **fully editable** — there is no
 * "edit in Joplin" affordance, no read-only branch (editor-chat.md §1.6).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { EditorView } from '@codemirror/view';
import { ErrorBoundary, Icon, LoadingState, InlineError, AutoroutedBadge } from '@/components';
import {
  updateNote,
  createNote,
  noteTitles,
  callEndpoint,
  getLogger,
  reextractNotes,
  addNoteTag,
  removeNoteTag,
  uploadAttachment,
} from '@/api';
import { toErrorEnvelope, useToastStore } from '@/hooks';
import type { NoteId, NotebookId, SaveResult, NoteView } from '@/types';
import { asNoteId, asNotebookId } from '@/types';
import { useNoteQuery } from '@/hooks/useNoteQuery';
import { useListNotebooksQuery, resolveNotebookPath } from '@/hooks/useNotebookQueries';
import { ROUTES, toEditor } from '@/routes';
import {
  useHighlightTargetStore,
  HIGHLIGHT_FRESHNESS_MS,
  useUiStore,
  EDITOR_MARGIN_MIN,
  EDITOR_MARGIN_MAX,
  EDITOR_MARGIN_DEFAULT,
} from '@/stores';
import { EditorViewHost } from './EditorViewHost';
import { EditorToolbar } from './EditorToolbar';
import { NoteHistoryPanel } from './NoteHistoryPanel';
import { nextNewNoteTitle } from './newNoteName';
import { RichTextToolbar } from './RichTextToolbar';
import { RichTextEditorPane } from './RichTextEditorPane';
// `<PreviewPane>` is no longer mounted (the rich-text view replaces the HTML
// preview's role in rich/split modes; code mode is now markdown-source only per
// the user's explicit ask). The component is preserved on disk in case it's
// useful later (e.g. a read-only export-preview affordance). `useScrollSync` is
// likewise unused at the surface level for now — the rich-text and CM6 panes
// scroll independently in split mode, which is what most markdown editors do.
import { useAutocompleteSources } from './autocomplete/useAutocompleteSources';
import { EditorStatusBar } from './EditorStatusBar';
// NotebooksTreePane was lifted out of EditorSurface — it's now a top-level
// shell pane (see AppShell.tsx NotebooksShellPane). The editor renders a
// 2-pane layout (note body + metadata sidecar) instead of the old 3-pane.
import { EditorMetaSidecar } from './EditorMetaSidecar';
import { NotePoolMetrics } from './NotePoolMetrics';
import type { DropHandlers } from './extensions/drop.ext';
import type { EditorConfig, EditorSaveState, DropOutcome } from './editorTypes';
import type { Editor } from '@tiptap/react';
import type { OutlineEntry } from './outline';

/** The editor's view modes — see `?view=` URL param. */
type EditorViewMode = 'rich' | 'split' | 'code';

const DEFAULT_VIEW_MODE: EditorViewMode = 'rich';

function parseViewMode(value: string | null): EditorViewMode {
  if (value === 'rich' || value === 'split' || value === 'code') return value;
  return DEFAULT_VIEW_MODE;
}

const log = getLogger('editor.surface');

/**
 * The in-app authoring surface. Loads the route's note, composes the split-pane editor +
 * preview, owns the save + drop routing, and renders the dirty/conflict status affordance.
 */
export function EditorSurface(): JSX.Element {
  // The route is `/editor/:noteId?` — an absent param is an unsaved scratch buffer.
  const { noteId: noteIdParam } = useParams<{ noteId: string }>();
  const noteId: NoteId | null = noteIdParam ? asNoteId(noteIdParam) : null;

  // Default landing = the "My Notes" table (the NotePoolMetrics empty-state below). Opening the
  // app (root → /editor with no note id) now shows the interactive notes table rather than
  // auto-resuming the last-opened note (Chris, 2026-06-23). We no longer redirect bare /editor to
  // `lastEditorNoteId` — but it's still recorded (below) and still drives stale-pointer recovery.
  // Refreshing while ON a note (/editor/{id}) keeps that note: the id is in the URL, so nothing
  // redirects. The "New" button creates a note and navigates to its id, also unaffected.
  const lastEditorNoteId = useUiStore((s) => s.lastEditorNoteId);
  const setLastEditorNoteId = useUiStore((s) => s.setLastEditorNoteId);
  const restoreNavigate = useNavigate();

  // Phase 12 — record every successful load as the new lastEditorNoteId so a
  // subsequent refresh restores to *this* note, not the one before it.
  useEffect(() => {
    if (noteId !== null) {
      setLastEditorNoteId(noteId);
    }
  }, [noteId, setLastEditorNoteId]);

  // Load the note WITH companion — v4 P3 (2026-05-18) needs companion.autoHashtags
  // for the doc-head .ed-tags chip strip. React Query dedupes the cache key, so the
  // <EditorMetaSidecar> call below shares the same fetch. Was `false` before P3.
  const noteQuery = useNoteQuery(noteId, true);
  // Notebook breadcrumb: resolve the note's notebook id → root→leaf title path over the flat
  // notebook list, so the editor crumb shows "Notebooks › Top › Sub › Title" — not a raw UUID.
  const notebooksList = useListNotebooksQuery();
  const notebookPath = useMemo(
    () =>
      resolveNotebookPath(
        noteQuery.data?.note.notebookId ?? null,
        notebooksList.data?.items ?? [],
      ),
    [noteQuery.data?.note.notebookId, notebooksList.data],
  );
  const queryClient = useQueryClient();

  // B5 — a 404 (`not_found`) on the loaded note id is a STALE POINTER, not a real
  // error to surface. It happens when the editor resolved a "most recently updated" /
  // last-opened note id (the restore-on-refresh redirect above, or the persisted
  // `lastEditorNoteId`) that points at a note which no longer exists (deleted between
  // sessions). The 404 surfaces as `code === 'not_found'` on the normalized envelope
  // (client.ts derives it from the HTTP 404 / backend stable code).
  const noteNotFound =
    noteId !== null &&
    noteQuery.isError &&
    toErrorEnvelope(noteQuery.error).code === 'not_found';

  // Phase 12 / B5 — stale-pointer recovery. If the resolved note 404s, clear the
  // persisted last-open record (so the next refresh doesn't loop back to the dead id)
  // and fall back gracefully to the bare scratch buffer (`/editor`) instead of leaving
  // the user on a red "note … does not exist / Try again" panel + retry loop. We
  // navigate with `replace` so the broken /editor/{deadId} URL doesn't sit in history.
  useEffect(() => {
    if (noteNotFound) {
      if (noteId === lastEditorNoteId) {
        setLastEditorNoteId(null);
      }
      restoreNavigate(ROUTES.editor, { replace: true });
    }
  }, [noteNotFound, noteId, lastEditorNoteId, setLastEditorNoteId, restoreNavigate]);

  // The autocomplete store — owned for the editor's lifetime; the completion extensions read
  // it synchronously, the status bar shows "tags refreshed N ago" from the snapshot.
  const { store: autocompleteStore, sources: autocompleteSources } = useAutocompleteSources();

  // The live buffer mirror — `<EditorViewHost>` pushes the CM6 doc here on every change so
  // `<PreviewPane>` re-renders. The CM6 view holds the *canonical* buffer; this is a
  // render-only mirror (editor-chat.md §1.2 — React state is not the buffer's source of truth).
  const [previewBody, setPreviewBody] = useState<string>('');

  // 2026-05-18 — fix the navigate-between-notes blank/stale body race.
  //
  // Previously: when noteId changed, EditorSurface re-rendered, loading
  // placeholder appeared (editor children unmount), fetch completed, editor
  // children remounted. RichTextEditorPane mounted reading `previewBody` —
  // which was STILL the previous note's body (state preserved across noteId
  // changes; only the children unmount during loading). EditorViewHost then
  // called `onDocChanged(initialDoc=newBody)` on its mount, which set
  // previewBody to the new body — but that's a state setter, takes a render.
  // For one frame the rich-text pane showed the stale body, or worse, an
  // empty body if previewBody started at '' (first navigation).
  //
  // Fix: track which noteId previewBody was last synced for. When the loaded
  // note differs from the synced one, force previewBody to the loaded body
  // immediately — bypasses the CM-mount→onDocChanged round-trip. On a
  // refetch for the SAME noteId we DON'T sync (preserves the user's in-flight
  // edits if they typed since the last save).
  const [lastSyncedNoteId, setLastSyncedNoteId] = useState<NoteId | null>(null);
  useEffect(() => {
    if (noteId === null) {
      // Scratch buffer — reset on entry so the previous note's body doesn't
      // bleed through into a fresh note creation.
      if (lastSyncedNoteId !== null) {
        setPreviewBody('');
        setLastSyncedNoteId(null);
      }
      return;
    }
    const loadedBody = noteQuery.data?.body;
    if (loadedBody === undefined) return; // fetch in flight; loading state shows
    if (noteId !== lastSyncedNoteId) {
      setPreviewBody(loadedBody);
      setLastSyncedNoteId(noteId);
    }
    // If noteId === lastSyncedNoteId, previewBody is current for this note
    // (possibly with user edits since load) — don't overwrite.
  }, [noteId, noteQuery.data?.body, lastSyncedNoteId]);

  // The editor's dirty/save state — mirrored from the save extension's transitions for the
  // status affordance (editor-chat.md §1.6 / §2.5).
  const [saveState, setSaveState] = useState<EditorSaveState>({ status: 'clean' });

  // B8 — reset the save status when the active note changes. The save state lives in
  // React state here (the save extension reports transitions through `onSaveStateChange`),
  // and React state PERSISTS across a note switch (only the keyed editor children remount —
  // see EditorViewHost's `key={noteId ?? 'scratch'}`). So a `save-failed` ("✕ save failed" /
  // "Couldn't save — will retry") banner from one note would linger after switching to a
  // different note and only clear once a later save succeeded. Clear it back to `clean` when
  // the active note id changes so each note starts from a fresh status; the remounted save
  // extension also begins at `clean` for the new note's baseline, so this is consistent.
  useEffect(() => {
    setSaveState({ status: 'clean' });
  }, [noteId]);

  // The most recent file-drop outcomes — surfaced in the status bar (editor-chat.md §3.1 — the
  // user always sees what happened to a dropped file; conventions.md §3 — no silent failures).
  const [dropOutcomes, setDropOutcomes] = useState<DropOutcome[]>([]);

  // Scroll-sync was wired against the CM6+PreviewPane split; with the preview
  // pane removed (rich-text view replaces it) there is no longer a second
  // scroller to keep in step. If a future split brings two scrollers back
  // (e.g. CM6 + rich-text both scrolling), reintroduce the sync here.

  // UX-reconciliation F4.1 — the active notebook id (set by clicking a notebook in the
  // tree, via `?notebookId=...`). Read at the TOP of the component so the hook order
  // stays stable across the loading/error/loaded branches (React Rules-of-Hooks: every
  // render must call the same hooks in the same order; an early return below makes
  // calling `useSearchParams` later illegal).
  const [searchParams, setSearchParams] = useSearchParams();

  // The view mode — rich text (default) / split / code. URL-encoded as `?view=` so a
  // saved link to a note remembers the user's preferred editing mode.
  const viewMode = useMemo<EditorViewMode>(
    () => parseViewMode(searchParams.get('view')),
    [searchParams],
  );
  const setViewMode = useCallback(
    (next: EditorViewMode) => {
      const params = new URLSearchParams(searchParams);
      if (next === DEFAULT_VIEW_MODE) params.delete('view');
      else params.set('view', next);
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  // --- the D-007 save path (editor-chat.md §2.5) -------------------------------------------
  // The injected save callback the save extension invokes. Issues `update_note` /
  // `PUT /api/v1/notes/{id}`; the backend sets `locallyModifiedAt` for a Joplin note and
  // returns the `SaveResult`. A scratch buffer (no note id) cannot be saved through here — the
  // editor would first need a `create_note`; that flow is the surface's "save new note"
  // affordance and out of this v1 path's scope, so a null-id save throws a clear error the
  // save extension surfaces as `save-failed`.
  const onSave = useCallback(
    async (body: string): Promise<SaveResult> => {
      if (noteId === null) {
        throw new Error(
          'This is an unsaved scratch buffer — create the note first before saving edits.',
        );
      }
      // `update_note` is the full-body replace (ADR 008). The backend owns the D-007 dirty
      // flag + conflict detection — the surface just sends the edit and reads the result.
      const { data } = await updateNote({ id: noteId, body });
      // Patch the cached NoteView body so a navigate-away-and-back serves the JUST-SAVED
      // text rather than the pre-edit cached copy. setQueryData (not invalidate) avoids a
      // refetch that would race the live editor; the value already matches what's on screen.
      // Covers both companion variants (the editor's includeCompanion=false body read and
      // the sidecar's =true read). This is what makes a longer note staleTime safe.
      queryClient.setQueriesData<NoteView>({ queryKey: ['note', noteId] }, (old) =>
        old ? { ...old, body } : old,
      );
      return data;
    },
    [noteId, queryClient],
  );

  // --- drop routing (editor-chat.md §1.2, §3.1) -------------------------------------------
  const dropHandlers: DropHandlers = useMemo(
    () => ({
      // A dropped `.md` file → the ingestion markdown-import endpoint. It is ingested as a
      // NEW note, never pasted into the current buffer (editor-chat.md §1.2).
      onMarkdownImport: async (file: File): Promise<DropOutcome> => {
        try {
          const text = await file.text();
          // `sources/markdown/import` is the ingestion bulk-import REST endpoint (ADR 008 /
          // scaffolding §3 Issue 8). The editor hands it the file content; ingestion owns the
          // actual note creation + Stage A enqueue.
          await callEndpoint('POST', 'sources/markdown/import', {
            body: { filename: file.name, content: text },
          });
          log.info('editor_md_drop_imported', { fileName: file.name });
          return { kind: 'markdown-import', fileName: file.name };
        } catch (err) {
          log.error('editor_md_drop_failed', { fileName: file.name, error: String(err) });
          return {
            kind: 'rejected',
            fileName: file.name,
            reason: 'The markdown file could not be imported.',
          };
        }
      },
      // A dropped non-`.md` file → upload it to the blob store (Attachments & media track) and
      // return its id; the drop extension inserts `![…](attachment:<id>)` at the drop point. A
      // scratch buffer has no note to attach to, so that case is `rejected`.
      onAttachment: async (file: File, dropNoteId: NoteId | null): Promise<DropOutcome> => {
        if (dropNoteId === null) {
          return {
            kind: 'rejected',
            fileName: file.name,
            reason: 'Save the note before attaching files to it.',
          };
        }
        try {
          const uploaded = await uploadAttachment(dropNoteId, file);
          log.info('editor_attachment_uploaded', {
            fileName: file.name,
            attachmentId: uploaded.attachmentId,
          });
          return {
            kind: 'attachment',
            fileName: file.name,
            noteId: dropNoteId,
            attachmentId: uploaded.attachmentId,
            isImage: (file.type || uploaded.mimeType).startsWith('image/'),
          };
        } catch (err) {
          log.error('editor_attachment_upload_failed', {
            fileName: file.name,
            error: String(err),
          });
          return {
            kind: 'rejected',
            fileName: file.name,
            reason: 'Upload failed — please try again.',
          };
        }
      },
      // Every drop's outcome is recorded so the status bar can render it.
      onOutcome: (outcome: DropOutcome) => {
        setDropOutcomes((prev) => [outcome, ...prev].slice(0, 5));
        // A save also refreshes the autocomplete data — a fresh markdown import means a new
        // note title; nudge the autocomplete store so wikilink completion picks it up.
        if (outcome.kind === 'markdown-import') {
          autocompleteStore.requestRefresh();
        }
      },
    }),
    [autocompleteStore],
  );

  // The save-state callback — also nudges the autocomplete store on a successful save (a save
  // can have changed the note's tags/title; editor-chat.md §2.2 — "refreshed on save").
  const onSaveStateChange = useCallback(
    (state: EditorSaveState) => {
      setSaveState(state);
      if (state.status === 'saved' || state.status === 'conflicted') {
        autocompleteStore.requestRefresh();
      }
    },
    [autocompleteStore],
  );

  // The live CM6 view — captured via EditorViewHost's `onViewReady` callback so the
  // formatting toolbar can dispatch transactions against it (wrap selection with
  // `**bold**`, prepend `# ` for a heading, etc.). React state (not just a ref) so
  // the toolbar's `disabled` flag re-renders when the view mounts/unmounts.
  const [editorView, setEditorView] = useState<EditorView | null>(null);
  const onViewReady = useCallback((view: EditorView | null) => {
    setEditorView(view);
  }, []);

  // 2026-05-18 — same pattern for the Tiptap editor. Captured via
  // RichTextEditorPane's onEditorReady callback so we can drive imperative
  // commands (the outline-pane jump-to-heading scroll, primarily).
  const [tiptapEditor, setTiptapEditor] = useState<Editor | null>(null);
  const onTiptapReady = useCallback(
    (ed: Editor | null) => {
      setTiptapEditor(ed);
    },
    [],
  );

  // Outline jump: scroll whichever editor is currently visible to the chosen
  // heading. In rich/split, that's Tiptap; in code, CM6. Split mode is the
  // tricky one — both editors are mounted; prefer Tiptap because the rich
  // pane is the user's reading view, but ALSO update CM6 so the source-pane
  // cursor lands at the corresponding line.
  const jumpToHeading = useCallback(
    (entry: OutlineEntry): void => {
      // CM6 scroll: line number → doc offset → scrollDOM.scrollTo at block top.
      if (editorView !== null) {
        try {
          const linePos = editorView.state.doc.line(entry.line);
          editorView.dispatch({
            selection: { anchor: linePos.from, head: linePos.from },
          });
          requestAnimationFrame(() => {
            const blockAt = editorView.lineBlockAt(linePos.from);
            editorView.scrollDOM.scrollTo({ top: blockAt.top, behavior: 'smooth' });
          });
        } catch {
          // line number out of bounds — body changed since outline was computed; no-op.
        }
      }
      // Tiptap scroll: walk doc descendants, find the Nth heading at the
      // matching level + text, scroll its DOM into view via coordsAtPos.
      // We match by (level, text) rather than ordinal because the same
      // body change that invalidated a CM6 line number probably didn't
      // change the heading itself.
      if (tiptapEditor !== null) {
        const doc = tiptapEditor.state.doc;
        let foundPos: number | null = null;
        doc.descendants((node, pos) => {
          if (foundPos !== null) return false;
          if (node.type.name !== 'heading') return true;
          const level = (node.attrs as { level?: number }).level ?? 0;
          if (level !== entry.level) return true;
          const text = node.textContent.trim();
          if (text !== entry.text.trim()) return true;
          foundPos = pos;
          return false;
        });
        if (foundPos !== null) {
          try {
            const coords = tiptapEditor.view.coordsAtPos(foundPos);
            // Walk up DOM to find the scrollable container, scroll heading to top.
            let scrollParent: HTMLElement | null = tiptapEditor.view.dom.parentElement;
            while (scrollParent !== null) {
              const ov = window.getComputedStyle(scrollParent).overflowY;
              if (ov === 'auto' || ov === 'scroll') break;
              scrollParent = scrollParent.parentElement;
            }
            if (scrollParent !== null) {
              const rect = scrollParent.getBoundingClientRect();
              const offset = coords.top - rect.top;
              scrollParent.scrollTo({
                top: scrollParent.scrollTop + offset - 12,
                behavior: 'smooth',
              });
            }
          } catch {
            // coordsAtPos can throw on invalid positions — ignore.
          }
        }
      }
    },
    [editorView, tiptapEditor],
  );

  // Two-way sync in split mode: when the user types in the RICH TEXT pane, it calls
  // `setPreviewBody(newMarkdown)`. CM6 ignores its `initialDoc` after mount, so we
  // need an effect that re-applies the canonical markdown into CM6 whenever
  // `previewBody` changes from outside CM6. The dispatch fires CM6's updateListener
  // which calls `setPreviewBody` again — but with the same value, so React bails
  // (Object.is) and no loop occurs.
  useEffect(() => {
    if (editorView === null) return;
    const current = editorView.state.doc.toString();
    if (current === previewBody) return;
    editorView.dispatch({
      changes: { from: 0, to: editorView.state.doc.length, insert: previewBody },
    });
  }, [previewBody, editorView]);

  // Rich-text pane's onChange — same setter as CM6's `onDocChanged`. The two paths
  // converge on `setPreviewBody` so a typed character in either editor updates the
  // canonical body, and the OTHER editor re-seeds via the effects above (and
  // RichTextEditorPane's own useEffect that detects external prop changes).
  const onRichTextChange = useCallback((markdown: string) => {
    setPreviewBody(markdown);
  }, []);

  // The same useNavigate handle as `restoreNavigate` above — react-router returns
  // a stable function reference per component, so aliasing for readability is fine.
  const navigate = restoreNavigate;

  // The active notebook id — set by clicking a notebook in the tree (it writes
  // `?notebookId=...`). The "New note" flow requires a notebook to create into;
  // when this is null the button is disabled (folder-first creation).
  const activeNotebookIdRaw = searchParams.get('notebookId');
  const activeNotebookId: NotebookId | null =
    activeNotebookIdRaw !== null && activeNotebookIdRaw.length > 0
      ? asNotebookId(activeNotebookIdRaw)
      : null;

  const pushToast = useToastStore((s) => s.push);
  // Guards a double-click while the create_note round-trip is in flight.
  const [creatingNote, setCreatingNote] = useState(false);

  // Task 2 (2026-05-21) — "New note" CREATES a real note in the currently
  // selected notebook, rather than opening an unsaveable scratch buffer. A
  // folder is REQUIRED: with no notebook selected we refuse + toast (the
  // button is also disabled, this is the keyboard / belt-and-braces guard).
  const onNewNote = useCallback(async () => {
    if (activeNotebookId === null) {
      pushToast({
        kind: 'warning',
        title: 'Select a notebook first',
        message: 'Pick a notebook in the tree to create a note inside it.',
      });
      return;
    }
    if (creatingNote) return;
    setCreatingNote(true);
    try {
      // Modal-less create (UX fix): name the note `New note NN` automatically and let the
      // user rename it inline in the tree. Scan existing titles so the sequence doesn't
      // collide; fall back to the default if the title fetch fails (create must not block).
      let title = 'New note 01';
      try {
        const titles = (await noteTitles({ limit: 1000 })).data.items.map((i) => i.title);
        title = nextNewNoteTitle(titles);
      } catch {
        /* keep the default — a name read failure must not stop note creation */
      }
      // createNote returns a WriteResult ({ noteId, extractionStatus }), NOT a
      // SaveResult — navigate to the new note id and let the tree refresh.
      const { data } = await createNote({
        title,
        body: '',
        notebook: activeNotebookId,
      });
      // Refresh the tree's note lists + the autocomplete title set so the new
      // note shows up: the per-notebook note list, the flat all-notes list the
      // pinned section reuses, and the note_titles caches.
      void queryClient.invalidateQueries({ queryKey: ['notebooks', 'notes'] });
      void queryClient.invalidateQueries({ queryKey: ['editor.tree.all_notes'] });
      void queryClient.invalidateQueries({ queryKey: ['note-titles'] });
      // Preserve the selected notebook in the URL so the tree stays anchored.
      navigate(toEditor(data.noteId) + `?notebookId=${activeNotebookId}`);
    } catch (err) {
      log.error('new_note_create_failed', {
        error: String(err),
        notebookId: activeNotebookId,
      });
      pushToast({
        kind: 'error',
        title: "Couldn't create note",
        message: 'The note could not be created. Please try again.',
      });
    } finally {
      setCreatingNote(false);
    }
  }, [activeNotebookId, creatingNote, navigate, pushToast, queryClient]);

  // 2026-05-18 — highlight target plumbing.
  //
  // Two consumers of `useHighlightTargetStore` exist:
  //   - NotebooksTreePane drives the BLUE row pulse + scroll-to-row +
  //     auto-expand. It reads the store directly (subscription).
  //   - EditorSurface (here) computes the OPTIONAL quoted-text excerpt to
  //     forward to RichTextEditorPane for its orange in-editor highlight.
  //     The actual highlight + scroll happens inside the editor via the
  //     QuoteHighlightExtension (ProseMirror decoration).
  //
  // We DELIBERATELY DO NOT clear the store target here. The freshness gate
  // (HIGHLIGHT_FRESHNESS_MS = 5s) + the noteId equality check inside each
  // consumer already prevent stale-target false-fires. A previous version
  // cleared, and that raced with NotebooksTreePane's subscription — see the
  // history of fix(highlight) commits in May 2026 for the painful unwinding.
  // 2026-05-18 — chip-visibility preference (persisted in useUiStore). The
  // toolbar button below toggles it; passed down to RichTextEditorPane so
  // both hashtag + entity decorators suppress when off.
  const chipsEnabled = useUiStore((s) => s.chipsEnabled);
  const setChipsEnabled = useUiStore((s) => s.setChipsEnabled);
  // Right-hand details pane (meta-sidecar) collapse — persisted in useUiStore.
  const metaCollapsed = useUiStore((s) => s.metaCollapsed);
  const setMetaCollapsed = useUiStore((s) => s.setMetaCollapsed);
  // Editor header (crumb + title + meta row + toolbar) collapse — reading/presentation
  // mode. Persisted in useUiStore, same as the side panes.
  const headCollapsed = useUiStore((s) => s.editorHeadCollapsed);
  const setHeadCollapsed = useUiStore((s) => s.setEditorHeadCollapsed);
  // In-note Find/Replace open state — lifted here so the toolbar's "Find in Note"
  // button and the rich pane's Ctrl/Cmd+F handler drive the same bar.
  const [findOpen, setFindOpen] = useState(false);

  // U8 — formatting-band visibility (rich/split). The Tiptap formatting toolbar
  // collapses while the user is just reading; it appears when the editor is
  // engaged (focused or holding a selection), while focus is inside the band
  // itself (block-type select, colour popover, link/image prompt modal), or when
  // pinned open via the "Aa" toggle. Deactivations are DELAYED (~160ms) so the
  // native blur→focus handoff between the editor and a toolbar control never
  // produces an intermediate collapsed frame (the focus-driven-toolbar hazard).
  const [fmtPinned, setFmtPinned] = useState(false);
  const [fmtBandFocus, setFmtBandFocus] = useState(false);
  const fmtBlurTimer = useRef<number | null>(null);
  const onFmtFocusCapture = useCallback(() => {
    if (fmtBlurTimer.current !== null) {
      window.clearTimeout(fmtBlurTimer.current);
      fmtBlurTimer.current = null;
    }
    setFmtBandFocus(true);
  }, []);
  const onFmtBlurCapture = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      // Focus moving WITHIN the band (button → select → popover) is not a leave.
      const next = e.relatedTarget as Node | null;
      if (next !== null && e.currentTarget.contains(next)) return;
      if (fmtBlurTimer.current !== null) window.clearTimeout(fmtBlurTimer.current);
      fmtBlurTimer.current = window.setTimeout(() => {
        fmtBlurTimer.current = null;
        setFmtBandFocus(false);
      }, 160);
    },
    [],
  );
  useEffect(
    () => () => {
      if (fmtBlurTimer.current !== null) window.clearTimeout(fmtBlurTimer.current);
    },
    [],
  );
  const fmtEngaged = useTiptapEngaged(tiptapEditor);
  const fmtOpen = fmtPinned || fmtBandFocus || fmtEngaged;

  // --- editor zoom + margin (rich view) -----------------------------------------------------
  const editorZoom = useUiStore((s) => s.editorZoom);
  const setEditorZoom = useUiStore((s) => s.setEditorZoom);
  const nudgeEditorZoom = useUiStore((s) => s.nudgeEditorZoom);
  const editorMargin = useUiStore((s) => s.editorMargin);
  const setEditorMargin = useUiStore((s) => s.setEditorMargin);
  // Ctrl/pinch-scroll zoom. A trackpad pinch fires wheel+ctrlKey in Chromium/WebView2, so this
  // one handler covers both gestures. React's synthetic onWheel is passive (can't preventDefault),
  // so we attach a NATIVE non-passive listener via a callback ref that (de)attaches with the body.
  const viewModeRef = useRef(viewMode);
  viewModeRef.current = viewMode;
  const zoomWheelCleanupRef = useRef<(() => void) | null>(null);
  const attachZoomWheel = useCallback(
    (el: HTMLDivElement | null) => {
      zoomWheelCleanupRef.current?.();
      zoomWheelCleanupRef.current = null;
      if (el === null) return;
      const onWheel = (e: WheelEvent): void => {
        if (!e.ctrlKey) return;
        if (viewModeRef.current === 'code') return; // zoom is a rich-view affordance
        e.preventDefault();
        // Both gestures arrive as wheel+ctrlKey but with very different magnitudes: a trackpad
        // pinch sends small deltas (~±5–15) while a Ctrl+scroll notch sends large ones (~±100).
        // A proportional step CAPPED per event keeps pinch responsive (it would otherwise move
        // imperceptibly) without Ctrl+scroll jumping the whole range in one notch. deltaY < 0
        // (scroll up / pinch out) → zoom in.
        const step = Math.max(-0.12, Math.min(0.12, -e.deltaY * 0.01));
        nudgeEditorZoom(step);
      };
      el.addEventListener('wheel', onWheel, { passive: false });
      zoomWheelCleanupRef.current = () => el.removeEventListener('wheel', onWheel);
    },
    [nudgeEditorZoom],
  );

  // Phase 3c of the 2026-05-18 extraction-remediation plan — "Reprocess" button
  // state. Tracks an in-flight re-extract POST so the button shows feedback
  // (disabled + "Reprocessing…" label) and surfaces success/failure in a brief
  // status badge that auto-clears after ~3s.
  const [reprocessState, setReprocessState] = useState<
    'idle' | 'pending' | 'success' | 'error'
  >('idle');
  // Version-history panel open state (2026-06-22). Opened from the toolbar "History" button;
  // disabled for unsaved scratch notes (noteId === null — a note with no id has no history yet).
  const [showHistory, setShowHistory] = useState(false);
  const onReprocess = useCallback(async () => {
    if (noteId === null) return;
    setReprocessState('pending');
    try {
      await reextractNotes({
        noteIds: [noteId],
        stage: 'C',
        // Re-run the LLM passes that produce tags + action items + LLM-typed
        // edges. NER / canonicalizer live on Stage A — a future enhancement
        // could expose a stage='A1' button too, but the user's most common
        // need ("clean up tags after a paste") is exactly the Stage C set.
        components: ['hashtags', 'action_items'],
      });
      setReprocessState('success');
    } catch (err) {
      log.error('reprocess_failed', { error: String(err), noteId });
      setReprocessState('error');
    }
    // Auto-clear the status badge so the button doesn't sit permanently in
    // success/error state — user keeps working.
    window.setTimeout(() => setReprocessState('idle'), 3_000);
  }, [noteId]);

  const highlightTarget = useHighlightTargetStore((s) => s.target);
  const isFreshQuoteForThisNote =
    highlightTarget !== null &&
    highlightTarget.noteId === noteId &&
    highlightTarget.quotedText.length > 0 &&
    Date.now() - highlightTarget.requestedAt < HIGHLIGHT_FRESHNESS_MS;
  const quoteToHighlight = isFreshQuoteForThisNote
    ? highlightTarget.quotedText
    : undefined;
  const quoteHighlightKey = isFreshQuoteForThisNote
    ? highlightTarget.requestedAt
    : undefined;

  // --- render ------------------------------------------------------------------------------
  // The shell (.ed-grid, NotebooksTreePane, meta sidecar) renders ALWAYS, regardless of
  // whether a note is loading or errored. The loading/error states live in the center
  // pane only — replacing the whole tree on every note-change caused NotebooksTreePane to
  // unmount + remount, losing the tree's expansion state (the bug the user reported:
  // "directories close every time I click a note").
  const note = noteQuery.data;
  const isLoadingNote = noteId !== null && noteQuery.isLoading;
  // B5 — a `not_found` is handled by the stale-pointer recovery effect above (it clears
  // the dead pointer and navigates to the scratch buffer), so we do NOT render the red
  // error panel + retry loop for it. Real errors (network/system) still surface inline.
  const isErrorNote = noteId !== null && noteQuery.isError && !noteNotFound;

  // Resolve the editor config. The editor is a faithful text surface — `initialDoc` is the
  // verbatim `get_note` body, no normalization (editor-chat.md §1.2). `readOnly` is ALWAYS
  // `false` — D-007, every note (incl. `source='joplin'`) is fully editable.
  const config: EditorConfig = {
    noteId,
    initialDoc: note?.body ?? '',
    source: note?.note.source ?? 'manual',
    readOnly: false,
  };

  // UX-reconciliation rip-and-replace + side-by-side WYSIWYG (2026-05-17):
  // the standalone's three-pane chrome (notebooks · note · meta) PLUS a
  // markdown↔rich-text view-mode toggle in the .ed-note-top. The center pane's
  // body switches between:
  //   - "rich"  — MDXEditor only (CM6 hidden but mounted; CM6 still owns autosave)
  //   - "split" — MDXEditor (left) + CM6 (right), both editable, live two-way sync
  //   - "code"  — CM6 only (the original markdown source mode)
  // In every mode the markdown body is the single source of truth — both editors
  // converge on `previewBody`, and the existing CM6 save extension handles the
  // autosave dance so save/dirty/conflict semantics stay unchanged.

  const sourceChip = config.source;
  const noteTitle = note ? note.note.title : 'New note';
  const showRich = viewMode === 'rich' || viewMode === 'split';

  // v4 P3 (2026-05-18, GAP_ANALYSIS.md §E2) — editable title input. The draft
  // state syncs from the loaded note; on blur, if it differs from what's
  // persisted, we PATCH the title via update_note. React Query's `note`
  // query is then invalidated so the rest of the UI (sidebar tree, status)
  // picks up the new title.
  const [titleDraft, setTitleDraft] = useState<string>(noteTitle);
  useEffect(() => {
    setTitleDraft(noteTitle);
  }, [noteTitle]);

  const onSaveTitle = useCallback(async () => {
    if (noteId === null) return; // scratch buffer — title save is a no-op
    const next = titleDraft.trim();
    if (next.length === 0) {
      // Empty title — revert to whatever's persisted; don't write an empty
      // string (could break Joplin sync expectations + UI ellipsis logic).
      setTitleDraft(noteTitle);
      return;
    }
    if (next === noteTitle) return; // unchanged — skip the round-trip
    try {
      await updateNote({ id: noteId, title: next });
      // Invalidate the note cache + EVERY surface that renders the title: the
      // notebook tree's per-notebook note list (['notebooks','notes',…]), the
      // note-titles list (autocomplete + command palette), the legacy flat-list,
      // the breadcrumb, and the right-rail. Without the first two, the tree row
      // kept the stale name after an editor-title edit.
      void queryClient.invalidateQueries({ queryKey: ['note', noteId] });
      void queryClient.invalidateQueries({ queryKey: ['notebooks', 'notes'] });
      void queryClient.invalidateQueries({ queryKey: ['note-titles'] });
      void queryClient.invalidateQueries({ queryKey: ['editor.tree.all_notes'] });
    } catch (err) {
      log.warn('title_save_failed', { noteId, error: String(err) });
      // Revert to persisted on failure so the UI doesn't lie.
      setTitleDraft(noteTitle);
    }
  }, [noteId, noteTitle, titleDraft, queryClient]);

  // Tag chips for the doc-head meta row. Both user-authored tags (manually added) and
  // extraction's auto-hashtags, from the same companion payload the sidecar reads.
  const autoTags = note?.companion?.autoHashtags ?? [];
  const userTags = note?.companion?.userTags ?? [];
  // Merge for display — user tags first, de-duped by path (a tag is either user or auto).
  const tags = useMemo(() => {
    const seen = new Set<string>();
    const merged: { tagPath: string; isAuto: boolean }[] = [];
    for (const t of userTags) {
      if (!seen.has(t.tagPath)) {
        seen.add(t.tagPath);
        merged.push({ tagPath: t.tagPath, isAuto: false });
      }
    }
    for (const t of autoTags) {
      if (!seen.has(t.tagPath)) {
        seen.add(t.tagPath);
        merged.push({ tagPath: t.tagPath, isAuto: true });
      }
    }
    return merged;
  }, [autoTags, userTags]);

  // Manual tag add / remove (2026-05-26) — real tag write endpoints (`add_note_tag` /
  // `remove_note_tag`) replace the old body-#hashtag hack, which never surfaced a chip
  // because Stage A's keyword scorer doesn't harvest a one-off literal #tag. `add_note_tag`
  // writes a user tag (is_auto=false) directly; remove works for user AND auto tags (the
  // backend records a suppression so a removed auto tag isn't re-added on re-extraction).
  const [tagDraft, setTagDraft] = useState<string>('');
  const [addingTag, setAddingTag] = useState<boolean>(false);
  const [tagBusy, setTagBusy] = useState<boolean>(false);

  const onAddTag = useCallback(async () => {
    const tag = tagDraft.trim().replace(/^#+/, '');
    setTagDraft('');
    setAddingTag(false);
    if (tag.length === 0 || noteId === null) return;
    setTagBusy(true);
    try {
      await addNoteTag({ noteId, tag });
      void queryClient.invalidateQueries({ queryKey: ['note', noteId] });
      autocompleteStore.requestRefresh();
    } catch (err) {
      log.warn('add_note_tag_failed', { noteId, error: String(err) });
      pushToast({
        kind: 'error',
        title: "Couldn't add tag",
        message: 'The tag could not be saved. Please try again.',
      });
    } finally {
      setTagBusy(false);
    }
  }, [tagDraft, noteId, queryClient, autocompleteStore, pushToast]);

  const onRemoveTag = useCallback(
    async (tagPath: string) => {
      if (noteId === null) return;
      setTagBusy(true);
      try {
        await removeNoteTag({ noteId, tag: tagPath });
        void queryClient.invalidateQueries({ queryKey: ['note', noteId] });
        autocompleteStore.requestRefresh();
      } catch (err) {
        log.warn('remove_note_tag_failed', { noteId, error: String(err) });
        pushToast({
          kind: 'error',
          title: "Couldn't remove tag",
          message: 'The tag change could not be saved. Please try again.',
        });
      } finally {
        setTagBusy(false);
      }
    },
    [noteId, queryClient, autocompleteStore, pushToast],
  );

  // Meta values for the doc-head meta row. `Updated` is relative; `Words`
  // counts the live body (so it ticks as the user types); `Source` shows
  // the same chip the status bar shows.
  const updatedLabel = note ? formatRelativeTime(note.note.updatedAt) : '';
  // Created is a fixed point, so show an absolute short date (not relative) next to Updated.
  const createdLabel = note ? formatCreatedDate(note.note.createdAt) : '';
  // Memoized on the body so it only re-counts when the (debounced) body changes,
  // not on every unrelated EditorSurface re-render (save-state ticks, hover, etc.).
  const wordsBody = previewBody || note?.body || '';
  const wordsLabel = useMemo(() => computeWordCount(wordsBody).toLocaleString(), [wordsBody]);

  // Empty state — no note selected. Render ONLY the corpus-metrics dashboard:
  // none of the editor chrome (the doc-head with title/crumb/meta/tags, the
  // toolbar with view-mode/chips/reprocess/save/New, the metadata sidecar)
  // applies when there's nothing to edit. The metrics surface owns the whole
  // pane and its own scroll. Loading/error never apply here — both require
  // `noteId !== null` — so this is the genuine "pick or create a note" state.
  if (noteId === null) {
    return (
      <div className="surface surface--editor surface--editor-empty">
        <NotePoolMetrics />
      </div>
    );
  }

  return (
    <div className="surface surface--editor">
      <div className={`ed-grid ed-two-pane${metaCollapsed ? ' ed-meta-collapsed' : ''}`}>
        {/* CENTER — the .ed-pane.ed-note column. v4 P3 (GAP_ANALYSIS.md §E1-E4):
            split into a .ed-doc-head (crumb + editable title + meta + tags) +
            .ed-doc-toolbar (format buttons + view-mode + save indicator + New).
            Previous layout was a single .ed-note-top row jammed with all of it. */}
        <section
          className={`ed-pane ed-note ed-center${headCollapsed ? ' ed-head-collapsed' : ''}`}
          data-view-mode={viewMode}
        >
          {/* Header collapse toggle — a slim shade-pull tab centered on the top edge.
              Hides the doc head + toolbar for distraction-free reading/presenting;
              the tab stays put so the header can be pulled back down. */}
          <button
            type="button"
            className="ed-head-toggle"
            onClick={() => setHeadCollapsed(!headCollapsed)}
            title={headCollapsed ? 'Show title and toolbar' : 'Hide title and toolbar'}
            aria-label={headCollapsed ? 'Show title and toolbar' : 'Hide title and toolbar'}
            aria-expanded={!headCollapsed}
          >
            <Icon name="chevron_right" size={12} />
          </button>
          <div className="ed-doc-head">
            {/* Breadcrumb — the note's directory position: Notebooks › Top › Sub › Title.
                Resolved from the flat notebook list (id→title→parent walk); shows the readable
                path, never a raw notebook UUID. Falls back to just the title when the note has
                no notebook (or while the notebook list is still loading). */}
            <div className="ed-crumb">
              {note?.note.notebookId != null && (
                <>
                  <Icon name="folder" size={11} />
                  <span>Notebooks</span>
                  {notebookPath.map((segment, i) => (
                    <span key={`nb-${i}`} className="ed-crumb__seg">
                      <span className="sep">›</span>
                      {segment}
                    </span>
                  ))}
                  <span className="sep">›</span>
                </>
              )}
              <span className="current">{noteTitle}</span>
            </div>

            {/* Editable title — `<input class="ed-title">` per v4 spec.
                onBlur fires update_note with the new title. */}
            <input
              type="text"
              className="ed-title"
              value={titleDraft}
              placeholder="Untitled note"
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => void onSaveTitle()}
              onKeyDown={(e) => {
                // Keep the title field a plain text input: stop the keystroke from bubbling to
                // any ancestor handler that treats Space/Enter as an activation key (which would
                // otherwise swallow the spacebar while typing a multi-word title).
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                } else if (e.key === 'Escape') {
                  setTitleDraft(noteTitle);
                  (e.target as HTMLInputElement).blur();
                }
              }}
              disabled={noteId === null}
              aria-label="Note title"
            />

            {/* Inline meta row — Updated · Words · Source · tag chips. The
                tags slot is a chip strip; each chip has a × to remove it and a
                dashed "+ add tag" affordance at the end opens an inline input
                (Task 4). Add/remove persist by editing the body's #tag tokens
                (there's no dedicated tag-mutation endpoint) and re-saving. */}
            <div className="ed-meta-row">
              {noteId !== null && <AutoroutedBadge noteId={noteId} />}
              {note !== undefined && (
                <>
                  <span className="ed-meta-row__item">
                    <span className="ed-meta-row__k">Updated</span>
                    <span className="ed-meta-row__v">{updatedLabel}</span>
                  </span>
                  <span className="ed-meta-row__item">
                    <span className="ed-meta-row__k">Created</span>
                    <span className="ed-meta-row__v">{createdLabel}</span>
                  </span>
                  <span className="ed-meta-row__item">
                    <span className="ed-meta-row__k">Words</span>
                    <span className="ed-meta-row__v">{wordsLabel}</span>
                  </span>
                  <span className="ed-meta-row__item">
                    <span className="ed-meta-row__k">Source</span>
                    <span className="ed-meta-row__v">{sourceChip}</span>
                  </span>
                </>
              )}
              <span className="ed-tags">
                {tags.map((t) => (
                  <span key={t.tagPath} className="ed-tag">
                    {t.tagPath}
                    <button
                      type="button"
                      className="ed-tag__remove"
                      title={`Remove #${t.tagPath}`}
                      aria-label={`Remove tag ${t.tagPath}`}
                      disabled={noteId === null || tagBusy}
                      onClick={() => void onRemoveTag(t.tagPath)}
                    >
                      ×
                    </button>
                  </span>
                ))}
                {addingTag ? (
                  <input
                    type="text"
                    className="ed-tag-input"
                    autoFocus
                    value={tagDraft}
                    placeholder="tag-name"
                    aria-label="New tag"
                    disabled={tagBusy}
                    onChange={(e) => setTagDraft(e.target.value)}
                    onBlur={() => void onAddTag()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void onAddTag();
                      } else if (e.key === 'Escape') {
                        setTagDraft('');
                        setAddingTag(false);
                      }
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="ed-tag ed-tag--add"
                    title={
                      noteId === null
                        ? 'Save the note first to add a tag'
                        : 'Add a #tag to this note'
                    }
                    disabled={noteId === null || tagBusy}
                    onClick={() => setAddingTag(true)}
                  >
                    + add tag
                  </button>
                )}
              </span>
            </div>
          </div>

          {/* U8 — the toolbar band. Row 1 (.ed-tb-actions) is ALWAYS visible:
              Find in Note / view-mode switch / Chips / History / Reprocess /
              saved / New (tb-spacer pushes the cluster flush right), plus the
              "Aa" formatting pin in rich+split. Row 2 (.ed-tb-fmt) holds the
              Tiptap formatting toolbar in rich/split and appears while the rich
              editor is engaged (focus / selection / pinned / focus inside the
              band), collapsing otherwise — the default reading state costs a
              single row. Code mode keeps the markdown-source toolbar inline in
              the actions row exactly as before (CM6 focus interplay untouched).
              The Tiptap toolbar stays MOUNTED when collapsed (hidden via CSS in
              richTextEditor.css) — its link/image prompt modal renders inline
              and must not unmount mid-flow. */}
          <div className="ed-doc-toolbar ed-doc-toolbar--stacked">
            <div className="ed-tb-actions">
            {showRich ? (
              <button
                type="button"
                className="btn btn--sm ed-fmt-pin"
                aria-pressed={fmtPinned}
                onClick={() => setFmtPinned(!fmtPinned)}
                title={
                  fmtPinned
                    ? 'Unpin the formatting tools (they auto-show while editing)'
                    : 'Pin the formatting tools open (they auto-show while editing)'
                }
              >
                Aa
              </button>
            ) : (
              <EditorToolbar view={editorView} />
            )}
            <div className="tb-spacer" />
            {/* "Find in Note" — opens the same in-note Find/Replace bar Ctrl/Cmd+F
                does; a visible affordance for users who don't know the shortcut. */}
            {showRich && (
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => setFindOpen(true)}
                title="Find in this note (Ctrl+F)"
              >
                <Icon name="search" size={13} /> Find in Note
              </button>
            )}
            <div
              className="tb-mode"
              role="radiogroup"
              aria-label="Editor view mode"
            >
              <button
                type="button"
                role="radio"
                aria-checked={viewMode === 'rich'}
                aria-pressed={viewMode === 'rich'}
                onClick={() => setViewMode('rich')}
                title="Rich text (WYSIWYG)"
              >
                Rich
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={viewMode === 'split'}
                aria-pressed={viewMode === 'split'}
                onClick={() => setViewMode('split')}
                title="Split (rich text + markdown source)"
              >
                Split
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={viewMode === 'code'}
                aria-pressed={viewMode === 'code'}
                onClick={() => setViewMode('code')}
                title="Markdown source"
              >
                Code
              </button>
            </div>
            {/* 2026-05-18 — inline-chip visibility toggle. When off, the
                rich pane suppresses hashtag + entity chip decorations
                (renders the underlying text plain). Persists across
                sessions via useUiStore. */}
            <button
              type="button"
              className="btn btn--sm"
              aria-pressed={chipsEnabled}
              onClick={() => setChipsEnabled(!chipsEnabled)}
              title={
                chipsEnabled
                  ? 'Hide inline hashtag + entity chips'
                  : 'Show inline hashtag + entity chips'
              }
            >
              {chipsEnabled ? 'Chips ✓' : 'Chips ✕'}
            </button>
            {/* Phase 3c (2026-05-18) — "Reprocess" button. Re-runs the LLM
                extraction passes (hashtags + action items) for the current note
                via the new `/api/v1/extraction/reextract` endpoint. Useful after
                a paste where the inferred tags / action items came back noisy or
                after an entity alias merge that should now propagate into this
                note's tags. Disabled for unsaved scratch notes (noteId === null). */}
            {/* Version history (2026-06-22) — opens the per-note revision panel
                (list / preview / restore). Disabled for unsaved scratch notes. */}
            <button
              type="button"
              className="btn btn--sm"
              disabled={noteId === null}
              onClick={() => setShowHistory(true)}
              title={
                noteId === null
                  ? 'Save the note first — history is kept for saved notes'
                  : 'View and restore previous versions of this note'
              }
            >
              <Icon name="clock" size={13} /> History
            </button>
            <button
              type="button"
              className="btn btn--sm"
              disabled={noteId === null || reprocessState === 'pending'}
              onClick={() => void onReprocess()}
              title={
                noteId === null
                  ? 'Save the note first — reprocess works on a saved note'
                  : 'Re-run the LLM extraction passes (hashtags + action items) for this note'
              }
            >
              {reprocessState === 'pending' && '⟳ Reprocessing…'}
              {reprocessState === 'success' && '✓ Reprocessed'}
              {reprocessState === 'error' && '✕ Reprocess failed'}
              {reprocessState === 'idle' && '⟳ Reprocess'}
            </button>
            <span
              className={saveStateClass(saveState.status)}
              title={`Save state: ${saveState.status}`}
            >
              {saveState.status === 'dirty' && '● unsaved'}
              {saveState.status === 'saving' && '… saving'}
              {saveState.status === 'saved' && '✓ saved'}
              {saveState.status === 'clean' && '✓ saved'}
              {saveState.status === 'conflicted' && '⚠ conflict'}
              {saveState.status === 'save-failed' && '✕ save failed'}
            </span>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => void onNewNote()}
              disabled={activeNotebookId === null || creatingNote}
              title={
                activeNotebookId === null
                  ? 'Select a notebook first to create a note'
                  : 'Create a new note in the selected notebook'
              }
            >
              <Icon name="plus" size={13} />{' '}
              {creatingNote ? 'Creating…' : 'New'}
            </button>
            </div>
            {showRich && tiptapEditor !== null && (
              <div
                className={`ed-tb-fmt${fmtOpen ? '' : ' ed-tb-fmt--collapsed'}`}
                onFocusCapture={onFmtFocusCapture}
                onBlurCapture={onFmtBlurCapture}
              >
                <RichTextToolbar editor={tiptapEditor} />
              </div>
            )}
          </div>

          <div className="ed-body" ref={attachZoomWheel}>
            {isLoadingNote && (
              <div className="cm-aletheia-editor-loading">
                <LoadingState label="Loading note…" />
              </div>
            )}
            {isErrorNote && (
              <div className="cm-aletheia-editor-error">
                <InlineError
                  error={toErrorEnvelope(noteQuery.error)}
                  onRetry={() => void noteQuery.refetch()}
                />
              </div>
            )}
            {/* The empty/no-note state is handled by an early return above
                (the full-bleed NotePoolMetrics dashboard) — by the time we
                render this center pane there is always a note id. */}
            {!isLoadingNote && !isErrorNote && (
              <>
                <div className="cm-aletheia-editor-split">
                  {/* Rich-text pane — mounts in rich / split modes. Controlled by
                      `previewBody`; emits markdown on every edit through
                      `onRichTextChange`, which converges on the same setter
                      CM6 uses. */}
                  {showRich && (
                    // 2026-05-18 — ErrorBoundary around the rich-text pane.
                    // MDXEditor has shown intermittent "flash then blank"
                    // behaviour on specific notes (likely a plugin parsing
                    // edge case). The boundary degrades a crash to a visible
                    // error panel instead of a silently-empty pane — the
                    // user can still switch to code mode to see + edit the
                    // body. Keyed on noteId so a fresh navigation resets
                    // any prior boundary capture for the previous note.
                    <ErrorBoundary
                      boundaryName="rich-text-pane"
                      key={`${noteId ?? 'scratch'}-boundary`}
                    >
                      <RichTextEditorPane
                        // key on noteId so navigation forces a clean
                        // remount of MDXEditor with the new note's
                        // markdown as its INITIAL value (vs trying to
                        // swap content imperatively in the mounted
                        // instance, which races with MDXEditor's own
                        // mount lifecycle for large/complex notes — the
                        // "blank in rich, fine in code" symptom). Fall
                        // back to the loaded body when previewBody
                        // hasn't synced yet (very brief first-render
                        // window).
                        key={noteId ?? 'scratch'}
                        noteId={noteId}
                        autocompleteStore={autocompleteStore}
                        markdown={previewBody || noteQuery.data?.body || ''}
                        onChange={onRichTextChange}
                        quoteToHighlight={quoteToHighlight}
                        quoteHighlightKey={quoteHighlightKey}
                        onEditorReady={onTiptapReady}
                        entities={
                          /* Map companion entities → EntityRef. Falls back to
                             [] when companion hasn't loaded yet OR the note has
                             no entities (gracefully renders no chips). */
                          (noteQuery.data?.companion?.entities ?? [])
                            .filter((e) => (e.surfaceForms?.length ?? 0) > 0)
                            .map((e) => ({
                              entityId: e.entityId,
                              surfaceForms: e.surfaceForms ?? [],
                            }))
                        }
                        chipsEnabled={chipsEnabled}
                        zoom={editorZoom}
                        marginPct={editorMargin}
                        findOpen={findOpen}
                        onFindOpenChange={setFindOpen}
                      />
                    </ErrorBoundary>
                  )}
                  {/* CM6 host — ALWAYS mounted (even in rich-only mode it is kept
                      mounted but visually hidden via CSS) because its save extension
                      owns the autosave/debounce + dirty tracking. Hiding instead
                      of unmounting keeps that machinery intact across mode toggles. */}
                  <div
                    className={
                      'cm-aletheia-host-wrap' +
                      (viewMode === 'rich' ? ' cm-aletheia-host-wrap--hidden' : '')
                    }
                  >
                    <EditorViewHost
                      key={noteId ?? 'scratch'}
                      config={config}
                      autocompleteStore={autocompleteStore}
                      onSave={onSave}
                      onSaveStateChange={onSaveStateChange}
                      dropHandlers={dropHandlers}
                      onDocChanged={setPreviewBody}
                      onViewReady={onViewReady}
                    />
                  </div>
                  {/* The HTML preview pane is no longer mounted — the rich-text view
                      replaces its role in rich + split modes, and code mode is now
                      "markdown source only" per the user's explicit ask. The
                      `<PreviewPane>` component is preserved on disk in case it's
                      useful later (e.g. a read-only export-preview affordance). */}
                </div>
                <EditorStatusBar
                  saveState={saveState}
                  source={config.source}
                  autocompleteRefreshedAt={autocompleteSources.refreshedAt}
                  tagCount={autocompleteSources.tags.length}
                  titleCount={autocompleteSources.titles.length}
                  dropOutcomes={dropOutcomes}
                />
                {/* View controls (margin + zoom) — only in the rich-text view (they shape the
                    rich document; code mode is monospace source). Zoom pairs with
                    Ctrl/pinch-scroll. */}
                {showRich && (
                  <EditorViewBar
                    zoom={editorZoom}
                    onZoom={setEditorZoom}
                    margin={editorMargin}
                    onMargin={setEditorMargin}
                  />
                )}
              </>
            )}
          </div>
        </section>

        {/* Collapse/expand toggle for the details pane — a small tab pinned to the
            right edge. Sits at the sidecar's left border when expanded (›, collapse)
            and at the far edge when collapsed (‹, expand). */}
        <button
          type="button"
          className="ed-meta-toggle"
          onClick={() => setMetaCollapsed(!metaCollapsed)}
          title={metaCollapsed ? 'Show details panel' : 'Hide details panel'}
          aria-label={metaCollapsed ? 'Show details panel' : 'Hide details panel'}
          aria-expanded={!metaCollapsed}
        >
          <Icon name="chevron_right" size={14} />
        </button>

        {/* RIGHT — metadata sidecar (Outline + Tags + Action items + Entities +
            Backlinks + Details). Composition only — every data point comes from an
            existing hook; the Outline is computed from the live body. Collapsed to the
            right via the `.ed-meta-collapsed` grid class (CSS), not unmounted, so its
            scroll/section state is preserved. */}
        <EditorMetaSidecar
          noteId={noteId}
          body={previewBody}
          onJumpToHeading={jumpToHeading}
        />
      </div>

      {/* Version-history panel (2026-06-22) — opened from the toolbar "History" button. */}
      {showHistory && noteId !== null && (
        <NoteHistoryPanel noteId={noteId} onClose={() => setShowHistory(false)} />
      )}
    </div>
  );
}

/** Relative-time formatter for the .ed-meta-row's "Updated" item. */
function formatRelativeTime(iso: string): string {
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs < 60_000) return 'just now';
    const m = Math.round(diffMs / 60_000);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 7) return `${d}d ago`;
    if (d < 30) return `${Math.round(d / 7)}w ago`;
    if (d < 365) return `${Math.round(d / 30)}mo ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

/** Absolute short date for the meta row's "Created" item ("Jun 9" / "Jun 9, 2025"). */
function formatCreatedDate(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const d = new Date(t);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** Word count for the .ed-meta-row's "Words" item — cheap; runs on every keystroke. */
function computeWordCount(body: string): number {
  if (body.length === 0) return 0;
  return body.split(/\s+/).filter((w) => w.length > 0).length;
}

/**
 * U8 — true while the Tiptap editor "wants" its formatting toolbar: it has focus
 * or holds a non-empty selection. Deactivation is delayed (~160ms) so the native
 * blur→focus handoff from the editor to a toolbar control (block-type select,
 * colour popover, prompt-modal input) never yields an intermediate collapsed
 * frame — by the time the recheck fires, either the editor re-focused (engaged
 * again) or the band's own focus-within state (tracked by the caller) holds it
 * open. Toolbar BUTTONS preventDefault on mousedown (they never blur the editor),
 * so the common click path doesn't even enter this window. Listens on the
 * editor's own event emitter; `<RichTextEditorPane>` reports `null` on unmount
 * (`onEditorReady(null)`), so this never runs against a destroyed editor.
 */
function useTiptapEngaged(editor: Editor | null): boolean {
  const [engaged, setEngaged] = useState(false);
  useEffect(() => {
    if (editor === null) {
      setEngaged(false);
      return undefined;
    }
    let timer: number | null = null;
    const compute = (): boolean =>
      editor.isFocused || !editor.state.selection.empty;
    const engage = (): void => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
      setEngaged(true);
    };
    const recheckSoon = (): void => {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        setEngaged(compute());
      }, 160);
    };
    const onFocus = (): void => engage();
    const onBlur = (): void => recheckSoon();
    const onSelection = (): void => {
      if (compute()) engage();
      else recheckSoon();
    };
    editor.on('focus', onFocus);
    editor.on('blur', onBlur);
    editor.on('selectionUpdate', onSelection);
    setEngaged(compute());
    return () => {
      editor.off('focus', onFocus);
      editor.off('blur', onBlur);
      editor.off('selectionUpdate', onSelection);
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [editor]);
  return engaged;
}

/** Save-state → CSS class for the .tb-save indicator. `clean`/`saved` are green
 *  (the .tb-save base color); `dirty`/`saving` are idle-gray; `conflicted`/
 *  `save-failed` are warn/bad respectively. */
function saveStateClass(status: EditorSaveState['status']): string {
  if (status === 'conflicted') return 'tb-save tb-save--warn';
  if (status === 'save-failed') return 'tb-save tb-save--bad';
  if (status === 'dirty' || status === 'saving') return 'tb-save tb-save--idle';
  return 'tb-save';
}

/**
 * `<EditorViewBar>` — the quiet footer row at the bottom of the editor (rich view only):
 * a margin slider (percent of pane width reserved per side; the content column autofits
 * the rest) and a zoom slider (50–200%, also driven by Ctrl/pinch-scroll). Each has a
 * clickable readout that resets to its default. The whole bar sits dimmed until hovered
 * or focused so it doesn't compete with the document.
 */
function EditorViewBar({
  zoom,
  onZoom,
  margin,
  onMargin,
}: {
  zoom: number;
  onZoom: (zoom: number) => void;
  margin: number;
  onMargin: (margin: number) => void;
}): JSX.Element {
  const pct = Math.round(zoom * 100);
  return (
    <div className="ed-view-bar" role="group" aria-label="Editor view controls">
      <span className="ed-view-bar__label" id="ed-margin-label">
        margin
      </span>
      <input
        type="range"
        className="ed-view-bar__slider"
        min={EDITOR_MARGIN_MIN}
        max={EDITOR_MARGIN_MAX}
        step={1}
        value={margin}
        aria-labelledby="ed-margin-label"
        title="Side margin (% of pane width per side)"
        onChange={(e) => onMargin(Number(e.target.value))}
      />
      <button
        type="button"
        className="ed-view-bar__readout"
        title="Reset margin"
        aria-label={`Reset margin to ${EDITOR_MARGIN_DEFAULT}%`}
        onClick={() => onMargin(EDITOR_MARGIN_DEFAULT)}
      >
        {margin}%
      </button>
      <span className="ed-view-bar__sep" aria-hidden="true" />
      <span className="ed-view-bar__label" id="ed-zoom-label">
        zoom
      </span>
      <input
        type="range"
        className="ed-view-bar__slider"
        min={50}
        max={200}
        step={10}
        value={pct}
        aria-labelledby="ed-zoom-label"
        title="Zoom (Ctrl + scroll)"
        onChange={(e) => onZoom(Number(e.target.value) / 100)}
      />
      <button
        type="button"
        className="ed-view-bar__readout"
        title="Reset to 100%"
        aria-label="Reset zoom to 100%"
        onClick={() => onZoom(1)}
      >
        {pct}%
      </button>
    </div>
  );
}

